'use client';

// BL-2473 — the mention pop-up's card: who mentioned you (initials and name),
// the as-sent body (the record line, then the start of the note) and two
// actions, View and Dismiss. With nobody hovering or focused on it, it closes
// on its own after 8 seconds, like Dismiss (BL-2734). Rendered through the shared Sonner `toast.custom`
// by `StaffMentionToastListener`. The card's chrome (white, bordered, shadowed)
// is the toaster's own toast class, which wraps a custom toast too, so this
// draws only what sits inside it.
//
// HTML SAFETY: the body is template markup with the note's text escaped at
// render (BL-2467), and it renders through `sanitizeInAppRichTextHtml`, the
// same allowlist the bidder's toast uses.

import { sanitizeInAppRichTextHtml } from '@auctogon/core/rich-text';
import { NO_STAFF_NAME_ON_FILE, staffInitials } from '@auctogon/core-admin/services/staff-names';

import Avatar from '../ui/Avatar';
import Button from '../ui/Button';
import { MENTION_TOAST_AUTO_CLOSE_MS, useMentionAutoClose } from './useMentionAutoClose';

/** What one pop-up shows, and what its two actions do. */
export interface MentionToastProps {
  /** Who mentioned the person; `null` renders the staff sentinel. */
  senderName: string | null;
  /** The row's as-sent toast body. */
  bodyHtml: string;
  /** Opens the note and closes the pop-up. */
  onView: () => void;
  /** Closes the pop-up and leaves the @Inbox item unread. */
  onDismiss: () => void;
  /** Closes the pop-up when its countdown runs out; leaves the item unread, like Dismiss. */
  onAutoClose: () => void;
}

/**
 * One mention pop-up.
 *
 * @param props - see {@link MentionToastProps}.
 * @returns the card.
 */
export default function MentionToast({ senderName, bodyHtml, onView, onDismiss, onAutoClose }: MentionToastProps) {
  const autoCloseRef = useMentionAutoClose({ ms: MENTION_TOAST_AUTO_CLOSE_MS, onExpire: onAutoClose });
  const name = senderName ?? NO_STAFF_NAME_ON_FILE;
  return (
    <div
      data-testid="mention-toast"
      role="status"
      ref={autoCloseRef}
      className="flex w-full gap-density-control"
    >
      <span data-testid="mention-toast-avatar" className="flex shrink-0">
        <Avatar initials={staffInitials(name)} size="md" tone="brand-soft" />
      </span>
      <div className="min-w-0 flex-1">
        {/* #111827 on #ffffff ................. 17.74:1  text ✓ (needs 4.5) */}
        <p data-testid="mention-toast-sender" className="truncate text-body font-semibold text-gray-900">
          {name}
        </p>
        {/* #374151 on #ffffff ................. 10.31:1  text ✓ (needs 4.5) */}
        <div
          data-testid="mention-toast-body"
          className="mt-0.5 line-clamp-3 break-words text-body text-gray-700"
          dangerouslySetInnerHTML={{ __html: sanitizeInAppRichTextHtml(bodyHtml) }}
        />
        <div className="mt-2.5 flex justify-end gap-density-cluster">
          <Button variant="outline" size="sm" onClick={onDismiss} data-testid="mention-toast-dismiss">
            Dismiss
          </Button>
          <Button size="sm" onClick={onView} data-testid="mention-toast-view">
            View
          </Button>
        </div>
      </div>
    </div>
  );
}
